import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Upload, FileText, Image as ImageIcon, Film, FileArchive, File as FileIcon,
  Link2, X, Plus, Download, ChevronRight, ChevronDown, ChevronLeft, ChevronRight as ChevronRightNav, Trash2, AlertTriangle,
  CheckCircle2, MessageSquare, CornerDownRight, Send, Pause, Play, RotateCcw,
  XCircle, RefreshCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTaskDate } from '../lib/dateFormat';
import { AiReviewBadge, AiReviewIssuesModal, useAiReviewPolling } from './SubmissionAiReview';
import { useTusUploads } from '../hooks/useTusUploads';
import { claimTusWithRetry } from '../lib/claimTusWithRetry';
import { TaskRichCommentBox, TaskCommentAttachments } from './TaskRichCommentBox';
import { taskAPI } from '../lib/api';

// Iter 64 — chunked TUS uploads now allow up to 2 GB per file.
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024;
const CONSOLIDATION_WINDOW_MS = 30 * 1000;
const IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp']);
const VIDEO_EXTS = new Set(['.mp4', '.mov', '.webm', '.avi', '.mkv']);
const PDF_EXTS = new Set(['.pdf']);

function fileTypeFromFilename(name = '') {
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase();
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (VIDEO_EXTS.has(ext)) return 'video';
  if (PDF_EXTS.has(ext)) return 'pdf';
  return 'other';
}

function humanSize(bytes) {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

const PLATFORM_META = {
  gdrive:  { label: 'Google Drive', color: 'text-yellow-300 bg-yellow-500/10 border-yellow-500/20' },
  dropbox: { label: 'Dropbox',      color: 'text-blue-300 bg-blue-500/10 border-blue-500/20' },
  figma:   { label: 'Figma',        color: 'text-violet-300 bg-violet-500/10 border-violet-500/20' },
  notion:  { label: 'Notion',       color: 'text-zinc-200 bg-zinc-500/10 border-zinc-500/20' },
  youtube: { label: 'YouTube',      color: 'text-red-300 bg-red-500/10 border-red-500/20' },
  vimeo:   { label: 'Vimeo',        color: 'text-teal-300 bg-teal-500/10 border-teal-500/20' },
  generic: { label: 'Link',         color: 'text-muted-foreground bg-foreground/[0.04] border-border/30' },
};

function FileTypeIcon({ filename, mime, className = 'h-5 w-5' }) {
  const kind = fileTypeFromFilename(filename);
  if (kind === 'image' || (mime || '').startsWith('image/')) return <ImageIcon className={className} />;
  if (kind === 'video' || (mime || '').startsWith('video/')) return <Film className={className} />;
  if (kind === 'pdf' || (mime || '') === 'application/pdf') return <FileText className={className} />;
  if ((filename || '').match(/\.(zip|rar|7z)$/i)) return <FileArchive className={className} />;
  return <FileIcon className={className} />;
}

// ──────────────────────────────────────────────────────────────────
// Upload modal — Iter 64: TUS resumable uploads, 2 GB max, 5-file batch,
// per-file progress / pause / cancel, resume-from-localStorage on reload.
// ──────────────────────────────────────────────────────────────────
function fmtSpeed(bps) {
  if (!bps || bps < 1024) return '—';
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${(bps / 1024 / 1024).toFixed(1)} MB/s`;
}
function fmtEta(sec) {
  if (!sec || sec < 1) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
function fmtBytes(b) {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

function UploadProgressRow({ item, onPause, onResume, onCancel, onRetry }) {
  const pct = item.total ? Math.min(100, Math.floor((item.offset / item.total) * 100)) : 0;
  const isActive = item.status === 'uploading' || item.status === 'starting';
  const isPaused = item.status === 'paused';
  const isDone = item.status === 'success';
  const isErr = item.status === 'error';
  const isCanc = item.status === 'cancelled';
  const barColor = isDone ? 'bg-emerald-500' : isErr ? 'bg-red-500' : isPaused ? 'bg-amber-500' : 'bg-gold';
  return (
    <div
      className="px-2.5 py-2 rounded-md bg-foreground/[0.03] border border-border/20"
      data-testid={`upload-row-${item.id}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <FileTypeIcon filename={item.file.name} mime={item.file.type} className="h-4 w-4 text-muted-foreground/70 shrink-0" />
        <span className="flex-1 truncate text-[12px]">{item.file.name}</span>
        <span className="text-[10px] text-muted-foreground/60 shrink-0 tabular-nums">
          {fmtBytes(item.offset)} / {fmtBytes(item.total)}
        </span>
        <span
          className="text-[10px] tabular-nums shrink-0 w-9 text-right"
          data-testid={`upload-pct-${item.id}`}
        >
          {pct}%
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full bg-foreground/[0.06] overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid={`upload-bar-${item.id}`}
        />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground/60">
        <span>
          {isActive && `${fmtSpeed(item.speedBps)} · ETA ${fmtEta(item.etaSec)}`}
          {isPaused && 'Paused'}
          {isDone && <span className="text-emerald-300">✓ Uploaded</span>}
          {isErr && <span className="text-red-300">{item.error || 'Failed'}</span>}
          {isCanc && 'Cancelled'}
        </span>
        <div className="flex items-center gap-1.5">
          {isActive && (
            <button
              onClick={() => onPause(item.id)}
              className="p-1 rounded hover:bg-foreground/[0.05]"
              data-testid={`upload-pause-${item.id}`}
              title="Pause"
            >
              <Pause className="h-3 w-3" />
            </button>
          )}
          {isPaused && (
            <button
              onClick={() => onResume(item.id)}
              className="p-1 rounded hover:bg-foreground/[0.05]"
              data-testid={`upload-resume-${item.id}`}
              title="Resume"
            >
              <Play className="h-3 w-3" />
            </button>
          )}
          {isErr && (
            <button
              onClick={() => onRetry(item.id)}
              className="p-1 rounded hover:bg-foreground/[0.05] text-amber-300"
              data-testid={`upload-retry-${item.id}`}
              title="Retry"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
          )}
          {!isDone && !isCanc && (
            <button
              onClick={() => onCancel(item.id)}
              className="p-1 rounded hover:bg-foreground/[0.05] text-muted-foreground/70"
              data-testid={`upload-cancel-${item.id}`}
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SubmissionUploadModal({
  open, onOpenChange, onSubmitted, workspaceId, taskId, lastSubmittedAt,
}) {
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const [note, setNote] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // 2026-02 — Retry status banner state, populated by claimTusWithRetry
  // BEFORE attempt 2 and attempt 3. Shape: { attempt, max } | null.
  const [retryStatus, setRetryStatus] = useState(null);
  const [resumablePrompt, setResumablePrompt] = useState(null); // { sessions: [...] } | null
  const inputRef = useRef(null);
  const tus = useTusUploads({ workspaceId, taskId });

  const reset = () => {
    setLinks([]); setLinkInput(''); setNote(''); setIsDragging(false); setFinalizing(false);
    setRetryStatus(null);
    tus.reset();
  };

  // Reset on close, scan for resumable sessions on open
  useEffect(() => {
    if (!open) { reset(); return; }
    // On open, ask the server which TUS sessions belong to this user/task
    // and offer to resume them. Avoids the awkward case of zombie chunks.
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/tus/sessions?workspace_id=${encodeURIComponent(workspaceId)}&task_id=${encodeURIComponent(taskId)}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
        );
        if (!r.ok) return;
        const list = await r.json();
        if (cancelled) return;
        if (Array.isArray(list) && list.length > 0) {
          setResumablePrompt({ sessions: list });
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const oversize = arr.filter(f => f.size > MAX_FILE_SIZE_BYTES);
    let ok = arr.filter(f => f.size <= MAX_FILE_SIZE_BYTES);
    if (oversize.length) {
      toast.error(
        `${oversize.length} file${oversize.length > 1 ? 's' : ''} exceed 2 GB — paste a link instead.`,
        { duration: 6000, description: oversize.map(f => f.name).join(', ') }
      );
    }
    // Cap to 5 concurrent uploads in this batch (per spec)
    const slotsLeft = 5 - tus.items.filter(i => i.status !== 'cancelled' && i.status !== 'error').length;
    if (ok.length > slotsLeft) {
      toast.warning(`Adding ${slotsLeft} of ${ok.length} files — max 5 per batch`);
      ok = ok.slice(0, Math.max(0, slotsLeft));
    }
    if (ok.length) tus.enqueue(ok);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const removeLink = (idx) => setLinks(p => p.filter((_, i) => i !== idx));

  const addLink = () => {
    const u = linkInput.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) {
      toast.error('Links must start with http:// or https://');
      return;
    }
    setLinks(p => [...p, u]);
    setLinkInput('');
  };

  const allDone = tus.items.length > 0 &&
    tus.items.every(i => i.status === 'success' || i.status === 'cancelled');
  const anyActive = tus.items.some(i => ['queued','starting','uploading','paused'].includes(i.status));
  const successIds = tus.items.filter(i => i.status === 'success' && i.sessionId).map(i => i.sessionId);

  const doFinalize = async () => {
    if (anyActive) {
      toast.error('Wait for uploads to finish before submitting');
      return;
    }
    if (!successIds.length && !links.length) {
      toast.error('Attach at least one file or link');
      return;
    }
    if (lastSubmittedAt) {
      const delta = Date.now() - new Date(lastSubmittedAt).getTime();
      if (delta >= 0 && delta < CONSOLIDATION_WINDOW_MS) {
        const go = window.confirm(
          'You just uploaded a version seconds ago. Create a new version anyway?\n\n' +
          'OK = create new version\nCancel = keep editing and add to the previous one later'
        );
        if (!go) return;
      }
    }
    setFinalizing(true);
    setRetryStatus(null);
    try {
      const body = await claimTusWithRetry(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/claim-tus`,
        {
          tus_session_ids: successIds,
          links,
          uploader_note: note.trim() || undefined,
        },
        { onAttempt: ({ attempt, max }) => setRetryStatus({ attempt, max }) },
      );
      toast.success(`Version ${body.version} uploaded`);
      onSubmitted?.(body);
      onOpenChange(false);
    } catch (e) {
      // Iter 64 hardening + 2026-02 client retry: keep staging dirs alive
      // for the 24 h cleanup window so the user can retry without
      // re-uploading 2 GB. We only get here when (a) the error is a
      // permanent 4xx OR (b) all 3 retries on a 5xx exhausted.
      const isGateway = e.status === 502 || e.status === 503 || e.status === 504;
      toast.error(`Finalize failed: ${e.message || 'unknown'}`, {
        description: isGateway
          ? `Tried ${e.attempts || 1} times. Storage is temporarily unhappy — paste a Drive / Dropbox link below and click Submit again to bypass the file upload, or retry in a minute (chunks kept for 24 h).`
          : e.attempts > 1
            ? `Tried ${e.attempts} times. Your uploaded chunks are kept on the server for 24 hours. Click Submit to retry.`
            : 'Your uploaded chunks are kept on the server for 24 hours. Click Submit to retry.',
      });
    } finally {
      setFinalizing(false);
      setRetryStatus(null);
    }
  };

  const acceptResumeAll = async () => {
    if (!resumablePrompt?.sessions?.length) return;
    // We can only resume IF the user re-picks each File handle (browser
    // security forbids reusing a File from a previous tab). For files that
    // were FULLY uploaded, we can claim them directly without re-picking.
    const fullyUploaded = resumablePrompt.sessions.filter(s => s.completed);
    const partials = resumablePrompt.sessions.filter(s => !s.completed);
    // Synthesize success-state items for fully-uploaded sessions so they
    // appear in the list and can be claimed via Submit.
    if (fullyUploaded.length) {
      // Use a tiny synthetic Blob as a placeholder — the file content is
      // already on the server, we just need the React state row.
      const synth = fullyUploaded.map(s => ({
        ...s,
      }));
      setResumablePrompt(null);
      // Push synthetic items via setItems hack — but cleaner: just track
      // resumed sessions in a separate ref and merge into successIds.
      window._stellarResumedSessions = synth;
      toast.success(`${synth.length} previously uploaded file${synth.length > 1 ? 's' : ''} ready to claim`);
    }
    if (partials.length) {
      toast.info(`${partials.length} partial upload${partials.length > 1 ? 's' : ''} — please re-select the file${partials.length > 1 ? 's' : ''} to resume`, {
        duration: 7000,
      });
    }
    if (!partials.length) setResumablePrompt(null);
  };

  const discardResumable = async () => {
    if (!resumablePrompt?.sessions?.length) return;
    const token = localStorage.getItem('stellar_token');
    await Promise.all(
      resumablePrompt.sessions.map(s =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/tus/${s.session_id}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Tus-Resumable': '1.0.0' },
        }).catch(() => null)
      )
    );
    setResumablePrompt(null);
    toast.success('Discarded prior upload session(s)');
  };

  // Merge any window._stellarResumedSessions into successIds at submit time
  const finalSessionIds = [
    ...successIds,
    ...((window._stellarResumedSessions || []).map(s => s.session_id)),
  ];
  // 2026-06-05 — UX fix. Many users typed a link, then clicked Submit
  // WITHOUT clicking "Add", so the link sat in `linkInput` and never made
  // it into `links`. The button stayed disabled and the user thought
  // "Submit requires a file". Treat a typed-and-valid pending URL as a
  // commitable link for the purposes of (a) enabling the button and
  // (b) auto-committing at submit time. The note field is and remains
  // ENTIRELY OPTIONAL — it never participates in this check.
  const pendingLinkTrimmed = (linkInput || '').trim();
  const pendingLinkValid = /^https?:\/\/\S+$/i.test(pendingLinkTrimmed);
  const hasAnyLink = links.length > 0 || pendingLinkValid;
  const hasAnyFile = finalSessionIds.length > 0;
  // Replace previous successIds reference for use in render
  const submitDisabled = anyActive || finalizing || (!hasAnyFile && !hasAnyLink);

  // Override doFinalize to use finalSessionIds
  const doFinalize2 = async () => {
    if (anyActive) {
      toast.error('Wait for uploads to finish before submitting');
      return;
    }
    // 2026-06-05 — auto-commit any valid pending link the user typed but
    // didn't click "Add" on. Keeps `links` as the single source of truth
    // for the backend payload while making the UX forgiving.
    let effectiveLinks = links;
    if (pendingLinkValid && !links.includes(pendingLinkTrimmed)) {
      effectiveLinks = [...links, pendingLinkTrimmed];
      setLinks(effectiveLinks);
      setLinkInput('');
    }
    if (!finalSessionIds.length && !effectiveLinks.length) {
      toast.error('Attach at least one file or link');
      return;
    }
    if (lastSubmittedAt) {
      const delta = Date.now() - new Date(lastSubmittedAt).getTime();
      if (delta >= 0 && delta < CONSOLIDATION_WINDOW_MS) {
        const go = window.confirm(
          'You just uploaded a version seconds ago. Create a new version anyway?\n\nOK = create new version\nCancel = keep editing'
        );
        if (!go) return;
      }
    }
    setFinalizing(true);
    setRetryStatus(null);
    try {
      const body = await claimTusWithRetry(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/claim-tus`,
        {
          tus_session_ids: finalSessionIds,
          links: effectiveLinks,
          // `note` is ENTIRELY OPTIONAL — empty string becomes `undefined`
          // and the backend treats absence as a non-error.
          uploader_note: note.trim() || undefined,
        },
        { onAttempt: ({ attempt, max }) => setRetryStatus({ attempt, max }) },
      );
      window._stellarResumedSessions = []; // claimed, drop the synthetic list
      toast.success(`Version ${body.version} uploaded`);
      onSubmitted?.(body);
      onOpenChange(false);
    } catch (e) {
      const isGateway = e.status === 502 || e.status === 503 || e.status === 504;
      toast.error(`Finalize failed: ${e.message || 'unknown'}`, {
        description: isGateway
          ? `Tried ${e.attempts || 1} times. Storage is temporarily unhappy — paste a Drive / Dropbox link below and click Submit again to bypass the file upload, or retry in a minute (chunks kept for 24 h).`
          : e.attempts > 1
            ? `Tried ${e.attempts} times. Uploaded chunks kept on the server for 24 hours. Click Submit to retry.`
            : 'Uploaded chunks kept on the server for 24 hours. Click Submit to retry.',
      });
    } finally {
      setFinalizing(false);
      setRetryStatus(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] bg-card border-border/40" data-testid="submission-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold">Upload submission</DialogTitle>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            Resumable upload — up to 5 files, max 2 GB per file. Uploads continue across page reloads.
          </p>
        </DialogHeader>

        {/* Resume prompt */}
        {resumablePrompt && resumablePrompt.sessions?.length > 0 && (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2.5"
            data-testid="resume-prompt"
          >
            <div className="flex items-start gap-2">
              <RotateCcw className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-amber-200">Resume previous upload?</p>
                <p className="text-[10.5px] text-amber-200/70 mt-0.5">
                  We found {resumablePrompt.sessions.length} unfinished upload{resumablePrompt.sessions.length > 1 ? 's' : ''} for this task.
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {resumablePrompt.sessions.map(s => (
                    <li key={s.session_id} className="text-[10.5px] text-amber-100/80 flex items-center gap-1.5">
                      <FileTypeIcon filename={s.filename} mime={s.mime_type} className="h-3 w-3" />
                      <span className="truncate">{s.filename}</span>
                      <span className="text-amber-100/50">
                        ({s.completed ? 'ready' : `${Math.floor((s.offset / s.total_size) * 100)}%`})
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={acceptResumeAll}
                    data-testid="resume-prompt-accept"
                    className="h-7 text-[11px]"
                  >
                    Resume / Claim
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={discardResumable}
                    data-testid="resume-prompt-discard"
                    className="h-7 text-[11px] text-amber-200/70"
                  >
                    Discard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drag-drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="submission-dropzone"
          className={
            'rounded-lg border border-dashed px-4 py-6 text-center cursor-pointer transition-colors ' +
            (isDragging ? 'border-gold/60 bg-gold/5' : 'border-border/40 bg-foreground/[0.02] hover:bg-foreground/[0.04]')
          }
        >
          <Upload className="h-6 w-6 mx-auto text-muted-foreground/50" />
          <p className="text-[12px] text-foreground/70 mt-2">
            Drop files here, or <span className="text-gold underline">click to browse</span>
          </p>
          <p className="text-[10px] text-muted-foreground/40 mt-1">
            Images · PDF · Video · Docs · Design files (Max 2 GB each, 5 per batch)
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="submission-file-input"
          />
        </div>

        {/* Per-file progress list */}
        {tus.items.length > 0 && (
          <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="submission-progress-list">
            {tus.items.map(it => (
              <UploadProgressRow
                key={it.id}
                item={it}
                onPause={tus.pause}
                onResume={tus.resume}
                onCancel={tus.cancel}
                onRetry={tus.retry}
              />
            ))}
          </div>
        )}

        {/* 2026-02-18 — Storage outage fallback hint. Tells the team they
            can submit a Drive / Dropbox / Figma LINK alone (no file upload
            required) while Emergent Object Storage is misbehaving. The
            link-only path has always worked at the API layer — this
            banner just makes it discoverable. Always visible (subtle), so
            the team doesn't have to remember the workaround mid-incident. */}
        <div
          className="rounded-md border border-sky-500/20 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-200/80 flex items-start gap-2"
          data-testid="submission-link-fallback-hint"
        >
          <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-sky-300/80" />
          <span>
            <strong className="text-sky-200">Tip:</strong> if a large file upload fails, paste a
            Google Drive / Dropbox / Figma link below instead — submissions accept link-only
            (no file required).
          </span>
        </div>

        {/* Link input */}
        <div>
          <label className="text-[11px] text-muted-foreground/70 flex items-center gap-1.5 mb-1.5">
            <Link2 className="h-3 w-3" /> Or paste a link (Drive, Dropbox, Figma, Notion, etc.)
          </label>
          <div className="flex gap-2">
            <Input
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLink())}
              placeholder="https://..."
              className="h-8 text-[12px] flex-1"
              data-testid="submission-link-input"
            />
            <Button size="sm" variant="outline" onClick={addLink} data-testid="submission-add-link">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
          {/* 2026-06-05 — UX hint: typed-but-not-yet-Added valid URL is
              honored on Submit. Visible only when the user has typed
              something that LOOKS like a URL but hasn't pressed Add. */}
          {pendingLinkValid && (
            <p className="text-[10px] text-gold/80 mt-1" data-testid="submission-pending-link-hint">
              Will be added on Submit — or press Enter / click "Add" now.
            </p>
          )}
        </div>

        {/* Link list */}
        {links.length > 0 && (
          <div className="space-y-1.5" data-testid="submission-link-list">
            {links.map((u, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-foreground/[0.03] text-[12px]">
                <Link2 className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0" />
                <span className="flex-1 truncate">{u}</span>
                <button onClick={() => removeLink(i)} className="text-muted-foreground/50 hover:text-destructive shrink-0">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Optional note */}
        <div>
          <label className="text-[11px] text-muted-foreground/70 block mb-1.5">
            Optional note (e.g., "V2 with updated headline per feedback")
          </label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe what changed in this version..."
            className="text-[12px] min-h-[60px]"
            data-testid="submission-note"
          />
        </div>

        <DialogFooter>
          {/* 2026-02 — Subtle non-alarming auto-retry status. Shown only
              while claimTusWithRetry is on attempt 2+ of MAX_ATTEMPTS. */}
          {retryStatus && (
            <div
              className="text-[11px] text-muted-foreground/80 mr-auto flex items-center gap-1.5"
              data-testid="finalize-retry-status"
              aria-live="polite"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400/80 animate-pulse" />
              Finalizing upload… (attempt {retryStatus.attempt}/{retryStatus.max})
            </div>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={finalizing}>Cancel</Button>
          <Button
            onClick={doFinalize2}
            disabled={submitDisabled}
            data-testid="submission-submit"
          >
            {finalizing
              ? (retryStatus ? `Finalizing… (attempt ${retryStatus.attempt}/${retryStatus.max})` : 'Finalizing…')
              : anyActive
                ? `Uploading… (${tus.items.filter(i => i.status === 'success').length}/${tus.items.length})`
                : 'Submit version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// File preview overlay (image / PDF iframe / video / download-only)
// ──────────────────────────────────────────────────────────────────
function FilePreviewOverlay({ file, onClose, downloadUrl, authedBlobUrl, onPrev, onNext, index, total, items = [], thumbBlobs = {}, onJump }) {
  // Gallery keyboard nav — ← / → flip between files without closing.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft' && onPrev) { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight' && onNext) { e.preventDefault(); onNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext]);
  if (!file) return null;
  const kind = fileTypeFromFilename(file.filename);
  const mime = file.mime_type || '';
  const isImage = kind === 'image' || mime.startsWith('image/');
  const isPdf = kind === 'pdf' || mime === 'application/pdf';
  const isVideo = kind === 'video' || mime.startsWith('video/');
  // P0 video fix (2026-06): direct streaming URLs. The backend accepts a
  // `?t=<jwt>` query token (get_current_user_media) so <video src> and the
  // download anchor can hit it without an Authorization header. The endpoint
  // serves 206 Partial Content so playback starts instantly and the browser's
  // download manager streams to disk with zero truncation risk.
  const token = localStorage.getItem('stellar_token') || '';
  const rel = downloadUrl || file.url || '';
  const absUrl = rel.startsWith('http') ? rel : `${process.env.REACT_APP_BACKEND_URL}${rel}`;
  const isUpload = file.type === 'upload';
  const streamUrl = isUpload ? `${absUrl}?t=${encodeURIComponent(token)}` : absUrl;
  const attachmentUrl = isUpload ? `${absUrl}?download=1&t=${encodeURIComponent(token)}` : absUrl;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-[80vw] sm:h-[85vh] bg-card border-border/40 p-0 overflow-hidden flex flex-col"
        data-testid="file-preview-overlay"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
          <div className="flex items-center gap-2 min-w-0">
            <FileTypeIcon filename={file.filename} mime={mime} className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            <span className="text-[12px] font-medium truncate">{file.filename}</span>
            {!!file.size_bytes && <span className="text-[10px] text-muted-foreground/50 shrink-0">· {humanSize(file.size_bytes)}</span>}
            {typeof index === 'number' && total > 1 && (
              <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums" data-testid="preview-gallery-counter">· {index + 1} of {total}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={attachmentUrl}
              download={file.filename}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gold/10 text-gold text-[11px] hover:bg-gold/15"
              data-testid="preview-download-btn"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </a>
            {/* Note: the close X is rendered by the Radix DialogContent itself
                (top-right corner). Adding a manual button here used to
                produce a duplicate X next to Download — removed. */}
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-background/50 flex items-center justify-center relative">
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/70 backdrop-blur border border-border/40 hover:bg-gold/15 hover:border-gold/40 text-foreground/80 hover:text-gold flex items-center justify-center"
              aria-label="Previous file"
              data-testid="preview-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/70 backdrop-blur border border-border/40 hover:bg-gold/15 hover:border-gold/40 text-foreground/80 hover:text-gold flex items-center justify-center"
              aria-label="Next file"
              data-testid="preview-next-btn"
            >
              <ChevronRightNav className="h-4 w-4" />
            </button>
          )}
          {isImage && authedBlobUrl && (
            <img src={authedBlobUrl} alt={file.filename} className="max-w-full max-h-full object-contain" />
          )}
          {isPdf && authedBlobUrl && (
            <iframe src={authedBlobUrl} title={file.filename} className="w-full h-full border-0" />
          )}
          {isVideo && (
            <video
              src={streamUrl}
              controls
              preload="metadata"
              className="max-w-full max-h-full"
              data-testid="video-preview-player"
            >
              Your browser cannot play this video. Use Download above.
            </video>
          )}
          {!isImage && !isPdf && !isVideo && (
            <div className="text-center p-10">
              <FileTypeIcon filename={file.filename} mime={mime} className="h-14 w-14 mx-auto text-muted-foreground/40" />
              <p className="text-[13px] text-foreground/80 mt-3">{file.filename}</p>
              <p className="text-[11px] text-muted-foreground/50 mt-1">
                Preview isn't available for this file type. Use Download above.
              </p>
            </div>
          )}
          {!authedBlobUrl && (isImage || isPdf) && (
            <div className="text-center p-10">
              <p className="text-[12px] text-muted-foreground/60">Loading preview…</p>
            </div>
          )}
        </div>
        {total > 1 && (
          <div className="border-t border-border/30 bg-card px-3 py-2 flex items-center gap-2 overflow-x-auto shrink-0" data-testid="preview-filmstrip">
            {items.map((f, i) => {
              const active = i === index;
              const thumb = thumbBlobs[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => onJump && onJump(f)}
                  title={f.filename}
                  className={`h-12 w-16 shrink-0 rounded-md overflow-hidden border flex items-center justify-center bg-background/60 transition-colors ${active ? 'border-gold ring-1 ring-gold/50' : 'border-border/30 opacity-60 hover:opacity-100 hover:border-gold/40'}`}
                  data-testid={`preview-filmstrip-item-${i}`}
                >
                  {thumb ? (
                    <img src={thumb} alt={f.filename} loading="lazy" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <FileTypeIcon filename={f.filename} mime={f.mime_type} className="h-5 w-5 text-muted-foreground/50" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// Inline comment thread (top-level + replies, one level deep)
// ──────────────────────────────────────────────────────────────────
// Phase 2 Group A — comment timestamps render in the viewer's LOCAL timezone.
// The relative-time computation is TZ-agnostic (delta of two epoch values),
// but the absolute fallback (anything older than a week) must show the
// viewer's wall-clock date, not a UTC slice.
function formatRelative(iso) {
  try {
    const then = new Date(iso).getTime();
    const delta = Date.now() - then;
    const min = Math.floor(delta / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const d = Math.floor(hr / 24);
    if (d < 7) return `${d}d`;
    // Older than a week — show absolute local date.
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) { return ''; }
}

function CommentRow({ comment, viewerId, canDeleteAny, canReply, onDelete, onReply, isReply }) {
  const canDelete = canDeleteAny || comment.author_id === viewerId;
  return (
    <div
      className={'flex gap-2 rounded px-1 py-0.5 ' + (isReply ? 'ml-6 border-l border-border/20 pl-3' : '')}
      data-testid={`submission-comment-${comment.id}`}
    >
      {isReply && <CornerDownRight className="h-3 w-3 text-muted-foreground/40 mt-1 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="font-medium">
            {comment.author?.removed ? 'removed user' : (comment.author?.name || comment.author_name || 'unknown')}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-muted-foreground/60">{formatRelative(comment.created_at)}</span>
          <div className="ml-auto flex items-center gap-2">
            {!isReply && canReply && (
              <button
                onClick={() => onReply(comment)}
                className="text-muted-foreground/50 hover:text-gold text-[10px]"
                data-testid={`comment-reply-${comment.id}`}
              >
                Reply
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => onDelete(comment)}
                className="text-muted-foreground/50 hover:text-destructive"
                title="Delete comment"
                data-testid={`comment-delete-${comment.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[12px] text-foreground/90 whitespace-pre-wrap break-words mt-0.5">
          {comment.text}
        </p>
        {/* 2026-07-29 CRITICAL FIX — voice notes / files were STORED on
            version comments but never rendered here, so they looked like
            they vanished. TaskCommentAttachments marks audio as a playable
            voice note, images/videos inline, docs as download chips. */}
        <TaskCommentAttachments attachments={comment.attachments} />
      </div>
    </div>
  );
}

function SubmissionCommentThread({
  comments, workspaceId, taskId, submissionId, viewerId,
  canTopLevel, canReply, canDeleteAny, isAssignee,
  onChanged, highlightCommentId,
}) {
  const [replyTo, setReplyTo] = useState(null); // {id, author_name}
  // 2026-07-29 — optimistic echo: a successfully-POSTed comment is shown
  // IMMEDIATELY from the API response, independent of the parent refetch.
  // Guarantees "either it appears or you get an error" — never a silent drop.
  const [localEcho, setLocalEcho] = useState([]);

  // Group into top-level + child map
  const { tops, repliesByParent } = useMemo(() => {
    const known = new Set((comments || []).map(c => c.id));
    const merged = [...(comments || []), ...localEcho.filter(e => !known.has(e.id))];
    const tops = [];
    const map = {};
    for (const c of merged) {
      const pid = c.in_reply_to || c.parent_comment_id;
      if (pid) {
        (map[pid] = map[pid] || []).push(c);
      } else {
        tops.push(c);
      }
    }
    tops.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''))
    );
    return { tops, repliesByParent: map };
  }, [comments, localEcho]);

  // Scroll + flash highlight the target comment when deep-linked
  useEffect(() => {
    if (!highlightCommentId) return;
    const el = document.querySelector(`[data-testid="submission-comment-${highlightCommentId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-gold/60', 'transition-shadow');
      setTimeout(() => el.classList.remove('ring-2', 'ring-gold/60'), 2400);
    }
  }, [highlightCommentId, comments]);

  const submitting_top_level = !replyTo;
  const topLevelBlocked = submitting_top_level && !canTopLevel;

  const postText = async (txt) => {
    if (!txt) return;
    // Frontend mirror of backend rule (still enforced on server)
    if (topLevelBlocked) {
      throw new Error('Only Owner/Admin/Manager can start a top-level thread — use Reply instead');
    }
    if (replyTo && !canReply) {
      throw new Error('You do not have permission to reply on this task');
    }
    const r = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submissionId}/comments`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify({ text: txt, in_reply_to: replyTo?.id || null }),
      }
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.detail || `Could not save comment (HTTP ${r.status}) — please try again`);
    }
    // Optimistic echo from the API response — appears even if refetch lags.
    try {
      const created = await r.json();
      if (created?.id) setLocalEcho(prev => [...prev, created]);
    } catch { /* refetch below still covers rendering */ }
    setReplyTo(null);
    onChanged?.();
  };

  // 2026-07-13 — attachments + voice notes go into THIS version's thread.
  const sendFileToThread = async (file, kind, txt) => {
    if (topLevelBlocked) {
      throw new Error('Only Owner/Admin/Manager can start a top-level thread — use Reply instead');
    }
    const res = await taskAPI.uploadSubmissionCommentAttachment(workspaceId, taskId, submissionId, file, {
      kind, text: txt || '', inReplyTo: replyTo?.id || '',
    });
    const created = res?.data;
    if (created?.id) setLocalEcho(prev => [...prev, created]);
    setReplyTo(null);
    onChanged?.();
  };

  const doDelete = async (c) => {
    if (!window.confirm('Delete this comment? Replies will also be removed.')) return;
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submissionId}/comments/${c.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(`Delete failed: ${e.message || 'unknown'}`);
    }
  };

  const disabledTopLevelTooltip =
    'Only Owner/Admin/Manager can start a top-level thread — use Reply to respond';

  return (
    <div className="mt-3 pt-3 border-t border-border/20" data-testid={`submission-thread-${submissionId}`}>
      <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] mb-2">
        <MessageSquare className="h-3 w-3" />
        <span>Comments ({tops.reduce((n, c) => n + 1 + (repliesByParent[c.id] || []).length, 0)})</span>
      </div>

      {tops.length > 0 && (
        <div className="space-y-2 mb-2">
          {tops.map(c => (
            <div key={c.id}>
              <CommentRow
                comment={c}
                viewerId={viewerId}
                canDeleteAny={canDeleteAny}
                canReply={canReply}
                onDelete={doDelete}
                onReply={(x) => { setReplyTo({ id: x.id, author_name: x.author?.name || x.author_name || '' }); }}
                isReply={false}
              />
              {(repliesByParent[c.id] || []).map(rc => (
                <div key={rc.id} className="mt-1.5">
                  <CommentRow
                    comment={rc}
                    viewerId={viewerId}
                    canDeleteAny={canDeleteAny}
                    canReply={false}
                    onDelete={doDelete}
                    onReply={() => {}}
                    isReply
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {replyTo && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground/70 mb-1">
          <CornerDownRight className="h-3 w-3" />
          <span>Replying to <span className="text-foreground/80">{replyTo.author_name || 'comment'}</span></span>
          <button
            onClick={() => setReplyTo(null)}
            className="ml-1 text-muted-foreground/50 hover:text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Composer — 2026-07-13: version-scoped RICH composer (text, file
          attachments, voice notes, drag-and-drop). Review feedback lives
          ONLY here, tied to this version. */}
      {(canTopLevel || canReply) && (
        <div
          title={(!replyTo && !canTopLevel) ? disabledTopLevelTooltip : undefined}
          data-testid={`composer-${submissionId}`}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const f = e.dataTransfer?.files?.[0];
            if (!f) return;
            try {
              await sendFileToThread(f, '', '');
              toast.success('Attachment sent');
            } catch (err) {
              toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
            }
          }}
        >
          <TaskRichCommentBox
            workspaceId={workspaceId}
            taskId={taskId}
            inputTestId={`submission-comment-input-${submissionId}`}
            disabled={topLevelBlocked}
            placeholder={
              replyTo
                ? 'Write a reply…'
                : (canTopLevel
                    ? 'Comment on this version — text, files, or voice…'
                    : (isAssignee
                        ? 'Tap Reply on a comment above to respond'
                        : 'You do not have comment permission'))
            }
            onSendText={postText}
            onSendFile={sendFileToThread}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Single version (expanded state)
// ──────────────────────────────────────────────────────────────────
function SubmissionVersion({
  submission, workspaceId, taskId, onDeleted, onChanged, viewerId,
  canApprove, canRemoveApproval, canRequestChanges, canClearChangesRequest,
  canReject, canClearRejection, canDeleteAny,
  canTopLevel, canReply, isAssignee, highlightCommentId,
  isExpanded, onToggleExpanded,
}) {
  const [previewFile, setPreviewFile] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [thumbBlobs, setThumbBlobs] = useState({});
  const [approveMenuOpen, setApproveMenuOpen] = useState(false);
  const [aiReviewFile, setAiReviewFile] = useState(null);
  // Iter 65a Section 4 — review action modals
  const [reviewModal, setReviewModal] = useState(null); // { kind: 'request_changes' | 'reject' }

  const retryAiReview = async (fileId) => {
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submission.id}/files/${fileId}/ai-review/retry`,
        { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success('AI review queued');
      onChanged?.();
    } catch (e) {
      toast.error(`Retry failed: ${e.message || 'unknown'}`);
    }
  };

  // Auth'd fetches for thumbnails (Authorization header, not anchor src)
  useEffect(() => {
    let revoked = [];
    const run = async () => {
      const token = localStorage.getItem('stellar_token');
      const next = {};
      for (const f of (submission.files || [])) {
        if (f.type === 'upload' && f.thumbnail_url) {
          try {
            const abs = f.thumbnail_url.startsWith('http') ? f.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${f.thumbnail_url}`;
            const r = await fetch(abs, { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) {
              const blob = await r.blob();
              const url = URL.createObjectURL(blob);
              revoked.push(url);
              next[f.id] = url;
            }
          } catch (e) { /* ignore */ }
        }
      }
      setThumbBlobs(next);
    };
    run();
    return () => revoked.forEach(URL.revokeObjectURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission.id]);

  useEffect(() => {
    if (!previewFile || previewFile.type !== 'upload') { setPreviewBlobUrl(null); return; }
    // P0 video fix (2026-06): videos stream directly via the <video> tag with
    // HTTP Range requests — NEVER blob-fetch them (a 161 MB fetch is exactly
    // what used to produce "Preview failed to load"). Only images/PDFs are
    // small enough for the fetch-and-blob path.
    const pk = fileTypeFromFilename(previewFile.filename);
    if (pk === 'video' || (previewFile.mime_type || '').startsWith('video/')) {
      setPreviewBlobUrl(null);
      return;
    }
    let alive = true;
    let objectUrl = null;
    setPreviewBlobUrl(null);
    (async () => {
      try {
        const token = localStorage.getItem('stellar_token');
        const abs = previewFile.url.startsWith('http') ? previewFile.url : `${process.env.REACT_APP_BACKEND_URL}${previewFile.url}`;
        const r = await fetch(abs, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        objectUrl = URL.createObjectURL(blob);
        if (alive) setPreviewBlobUrl(objectUrl);
      } catch (e) {
        toast.error('Preview failed to load');
      }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [previewFile]);

  const doDelete = async () => {
    if (!window.confirm(`Delete V${submission.version}? This removes its files from storage.`)) return;
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submission.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success(`V${submission.version} deleted`);
      onDeleted?.();
    } catch (e) {
      toast.error(`Delete failed: ${e.message || 'unknown'}`);
    }
  };

  const canSelfDelete = submission.uploaded_by === viewerId &&
    (Date.now() - new Date(submission.uploaded_at).getTime() < 5 * 60 * 1000);
  const canDeleteVersion = canSelfDelete || canApprove;

  // Iter 65a Section 4 — derived state pill + RBAC for review actions
  const approvalState = submission.approval_state ||
    (submission.approved ? 'approved' : 'pending');
  const isApproved = approvalState === 'approved';
  const isChangesRequested = approvalState === 'changes_requested';
  const isRejected = approvalState === 'rejected';
  // Pending-state buttons should hide once a terminal state is in place. The
  // primary actions only show when no review action exists yet — clear-state
  // buttons take over otherwise. Mirrors the backend state-machine guards.
  const showApproveBtn = canApprove && !isApproved && !isRejected && !isChangesRequested;
  const showRequestChangesBtn = canRequestChanges && !isApproved && !isRejected && !isChangesRequested;
  const showRejectBtn = canReject && !isApproved && !isRejected && !isChangesRequested;
  const showClearChangesBtn = canClearChangesRequest && isChangesRequested;
  const showClearRejectionBtn = canClearRejection && isRejected;

  const callReviewAction = async (path, payload) => {
    const r = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submission.id}/${path}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify(payload || {}),
      }
    );
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.detail || `HTTP ${r.status}`);
    }
    return r.json();
  };

  // 2026-07-13 — one smooth flow, NO popup: status flips immediately and
  // the reviewer lands in this version's comment box for feedback
  // (text / attachments / voice).
  const doRequestChanges = async () => {
    try {
      await callReviewAction('request-changes', { note: '' });
      toast.success(`Changes requested on V${submission.version} — add your feedback below`);
      onChanged?.();
      if (!isExpanded && onToggleExpanded) onToggleExpanded();
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="submission-comment-input-${submission.id}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          el.focus();
        }
      }, 400);
    } catch (e) {
      toast.error(`Request changes failed: ${e.message || 'unknown'}`);
    }
  };
  const doClearChangesRequest = async () => {
    try {
      await callReviewAction('clear-changes-request');
      toast.success(`Cleared on V${submission.version}`);
      onChanged?.();
    } catch (e) {
      toast.error(`Clear failed: ${e.message || 'unknown'}`);
    }
  };
  const doReject = async ({ reason, alsoReopen = false }) => {
    try {
      await callReviewAction('reject', { reason, also_reopen: !!alsoReopen });
      toast.success(alsoReopen
        ? `V${submission.version} rejected — task reopened`
        : `V${submission.version} rejected`);
      setReviewModal(null);
      onChanged?.();
    } catch (e) {
      toast.error(`Reject failed: ${e.message || 'unknown'}`);
    }
  };
  const doClearRejection = async () => {
    try {
      await callReviewAction('clear-rejection');
      toast.success(`Rejection cleared on V${submission.version}`);
      onChanged?.();
    } catch (e) {
      toast.error(`Clear failed: ${e.message || 'unknown'}`);
    }
  };

  const doApprove = async ({ alsoComplete = false } = {}) => {
    setApproveMenuOpen(false);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submission.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
          },
          body: JSON.stringify({ also_complete: !!alsoComplete }),
        }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success(alsoComplete ? `V${submission.version} approved — task completed` : `V${submission.version} approved`);
      onChanged?.();
      // BUG-07 (2026-05-23) — global event so EVERY surface that caches
      // task state (Tasks list, Calendar, Dashboard widgets, Person KPIs,
      // Reviews queue) refetches without requiring a page reload.
      try {
        window.dispatchEvent(new CustomEvent('stellar-task-changed', {
          detail: { task_id: taskId, workspace_id: workspaceId, kind: alsoComplete ? 'approved-and-completed' : 'approved' }
        }));
      } catch {}
    } catch (e) {
      toast.error(`Approve failed: ${e.message || 'unknown'}`);
    }
  };

  const doRemoveApproval = async () => {
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/${submission.id}/approve`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` } }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      toast.success(`Approval removed from V${submission.version}`);
      onChanged?.();
      try {
        window.dispatchEvent(new CustomEvent('stellar-task-changed', {
          detail: { task_id: taskId, workspace_id: workspaceId, kind: 'approval-removed' }
        }));
      } catch {}
    } catch (e) {
      toast.error(`Failed: ${e.message || 'unknown'}`);
    }
  };

  const uploads = (submission.files || []).filter(f => f.type === 'upload');
  const externalLinks = (submission.files || []).filter(f => f.type === 'link');

  return (
    <div className="rounded-lg border border-border/30 bg-foreground/[0.02] p-3" data-testid={`submission-v${submission.version}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex items-center gap-2 flex-wrap text-left min-w-0 flex-1 hover:opacity-90"
          aria-expanded={isExpanded}
          data-testid={`submission-v${submission.version}-toggle`}
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
          )}
          <span className="text-[12px] font-semibold">Version {submission.version}</span>
          <span className="text-[10px] text-muted-foreground/50">·</span>
          <span className="text-[11px] text-muted-foreground/70">
            {submission.uploader?.removed ? 'removed user' : submission.uploader?.name || 'unknown'}
          </span>
          <span className="text-[10px] text-muted-foreground/50">· {formatTaskDate(submission.uploaded_at)}</span>
          {/* State pill — only one of these renders at a time */}
          {isApproved && (
            <Badge className="text-[9px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30 flex items-center gap-1" data-testid={`submission-v${submission.version}-approved-badge`}>
              <CheckCircle2 className="h-2.5 w-2.5" />
              Approved{submission.approver?.name ? ` by ${submission.approver.name}` : ''}
            </Badge>
          )}
          {isChangesRequested && (
            <Badge className="text-[9px] bg-amber-500/15 text-amber-300 border-amber-500/30 flex items-center gap-1" data-testid={`submission-v${submission.version}-changes-requested-badge`}>
              <AlertTriangle className="h-2.5 w-2.5" />
              Changes requested{submission.change_requester?.name ? ` by ${submission.change_requester.name}` : ''}
            </Badge>
          )}
          {isRejected && (
            <Badge className="text-[9px] bg-red-500/15 text-red-300 border-red-500/30 flex items-center gap-1" data-testid={`submission-v${submission.version}-rejected-badge`}>
              <XCircle className="h-2.5 w-2.5" />
              Rejected{submission.rejecter?.name ? ` by ${submission.rejecter.name}` : ''}
            </Badge>
          )}
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {/* Approve controls: "Approve" is primary, chevron opens "Approve & mark Complete".
              Currently-approved version shows "Remove approval" instead. */}
          {showApproveBtn && (
            <div className="relative inline-flex items-stretch">
              <button
                onClick={() => doApprove({ alsoComplete: false })}
                className="inline-flex items-center gap-1 pl-2 pr-2 py-1 rounded-l-md text-[10px] font-medium bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15 border border-emerald-500/30"
                data-testid={`submission-v${submission.version}-approve`}
              >
                <CheckCircle2 className="h-3 w-3" />
                Approve
              </button>
              <button
                onClick={() => setApproveMenuOpen(o => !o)}
                className="px-1 py-1 rounded-r-md border border-l-0 border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                aria-label="More approve options"
                data-testid={`submission-v${submission.version}-approve-chevron`}
              >
                <ChevronDown className="h-3 w-3" />
              </button>
              {approveMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-[260px] rounded-md border border-border/40 bg-card shadow-lg z-40 pointer-events-auto"
                  onMouseLeave={() => setApproveMenuOpen(false)}
                  data-testid={`submission-v${submission.version}-approve-menu`}
                >
                  <button
                    onClick={() => doApprove({ alsoComplete: true })}
                    className="w-full text-left px-3 py-2 text-[11px] hover:bg-foreground/[0.04] flex items-center gap-2"
                    data-testid={`submission-v${submission.version}-approve-and-complete`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium whitespace-nowrap">Approve & mark Complete</div>
                      <div className="text-muted-foreground/60 text-[10px]">Closes the task in one click</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          {showRequestChangesBtn && (
            <button
              onClick={doRequestChanges}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-500/10 text-amber-300 hover:bg-amber-500/15 border border-amber-500/30"
              data-testid={`submission-v${submission.version}-request-changes`}
            >
              <AlertTriangle className="h-3 w-3" />
              Request changes
            </button>
          )}
          {showRejectBtn && (
            <button
              onClick={() => setReviewModal({ kind: 'reject' })}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-red-500/10 text-red-300 hover:bg-red-500/15 border border-red-500/30"
              data-testid={`submission-v${submission.version}-reject`}
            >
              <XCircle className="h-3 w-3" />
              Reject
            </button>
          )}
          {showClearChangesBtn && (
            <button
              onClick={doClearChangesRequest}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-foreground/[0.04] text-muted-foreground/80 hover:bg-foreground/[0.07] border border-border/30"
              data-testid={`submission-v${submission.version}-clear-changes-request`}
            >
              <RefreshCcw className="h-3 w-3" />
              Clear request
            </button>
          )}
          {showClearRejectionBtn && (
            <button
              onClick={doClearRejection}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-foreground/[0.04] text-muted-foreground/80 hover:bg-foreground/[0.07] border border-border/30"
              data-testid={`submission-v${submission.version}-clear-rejection`}
            >
              <RefreshCcw className="h-3 w-3" />
              Clear rejection
            </button>
          )}
          {canRemoveApproval && isApproved && (
            <button
              onClick={doRemoveApproval}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-foreground/[0.04] text-muted-foreground/80 hover:bg-foreground/[0.07] border border-border/30"
              data-testid={`submission-v${submission.version}-remove-approval`}
            >
              Remove approval
            </button>
          )}
          {canDeleteVersion && (
            <button
              onClick={doDelete}
              className="text-muted-foreground/50 hover:text-destructive"
              title={canSelfDelete ? 'Delete (within 5 min of upload)' : 'Delete (owner/admin)'}
              data-testid={`submission-v${submission.version}-delete`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* State-detail strip — surfaces note/reason inline with the row header */}
      {isChangesRequested && submission.changes_requested_note && (
        <div
          className="text-[11.5px] text-amber-200/90 bg-amber-500/[0.06] border-l-2 border-amber-500/40 rounded px-2.5 py-1.5 mb-2 whitespace-pre-wrap"
          data-testid={`submission-v${submission.version}-changes-requested-note`}
        >
          <span className="font-semibold">Changes requested:</span> {submission.changes_requested_note}
        </div>
      )}
      {isRejected && submission.rejected_reason && (
        <div
          className="text-[11.5px] text-red-200/90 bg-red-500/[0.06] border-l-2 border-red-500/40 rounded px-2.5 py-1.5 mb-2 whitespace-pre-wrap"
          data-testid={`submission-v${submission.version}-rejected-reason`}
        >
          <span className="font-semibold">Rejected:</span> {submission.rejected_reason}
        </div>
      )}

      {isExpanded && submission.uploader_note && (
        <p className="text-[12px] text-foreground/80 italic bg-foreground/[0.03] rounded px-2.5 py-1.5 mb-3 whitespace-pre-wrap">
          {submission.uploader_note}
        </p>
      )}

      {/* File grid — bigger thumbnails (Part 3):
          1 col mobile, 2 col ≥sm. aspect-[4/3] + object-contain so designers can
          actually recognise a pizza shot from a QR code. */}
      {isExpanded && uploads.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {uploads.map(f => {
            const thumb = thumbBlobs[f.id];
            return (
              <div
                key={f.id}
                className="group aspect-[4/3] rounded-lg border border-border/30 bg-background/40 overflow-hidden relative flex items-center justify-center hover:border-gold/40 transition-colors"
                data-testid={`submission-file-${f.id}`}
                title={f.filename}
              >
                <button
                  onClick={() => setPreviewFile(f)}
                  className="absolute inset-0 flex items-center justify-center"
                  data-testid={`submission-file-open-${f.id}`}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={f.filename}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <FileTypeIcon filename={f.filename} mime={f.mime_type} className="h-12 w-12 text-muted-foreground/40" />
                  )}
                </button>
                {/* AI review badge — top-right corner overlay.
                    D88+++ (2026-06-03): hidden for video files because the
                    backend no longer runs AI review on videos per owner
                    decision. Defence-in-depth in case a stale ai_review
                    object lingers on a file before the startup-strip sweep
                    runs. */}
                {!(f.mime_type || '').toLowerCase().startsWith('video/') && (
                  <div className="absolute top-1.5 right-1.5 z-10">
                    <AiReviewBadge
                      aiReview={f.ai_review}
                      onClick={() => setAiReviewFile(f)}
                      onRetry={() => retryAiReview(f.id)}
                    />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 pointer-events-none">
                  <p className="text-[11px] text-white/90 truncate font-medium">{f.filename}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isExpanded && externalLinks.length > 0 && (
        <div className="space-y-1.5">
          {externalLinks.map(l => {
            const meta = PLATFORM_META[l.platform] || PLATFORM_META.generic;
            return (
              <a
                key={l.id}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-[11.5px] truncate hover:opacity-90 ${meta.color}`}
                data-testid={`submission-link-${l.id}`}
              >
                <Link2 className="h-3.5 w-3.5 shrink-0" />
                <Badge variant="outline" className="text-[9px] border-current/20 shrink-0">{meta.label}</Badge>
                <span className="truncate">{l.url}</span>
              </a>
            );
          })}
        </div>
      )}

      {/* Collapsed-state file/comment counts — shown inline beneath header
          when the version body is hidden. Lets reviewers triage at a glance. */}
      {!isExpanded && (
        <div
          className="text-[10.5px] text-muted-foreground/60 pl-5 -mt-1"
          data-testid={`submission-v${submission.version}-summary-counts`}
        >
          {(submission.files || []).length} {(submission.files || []).length === 1 ? 'file' : 'files'}
          {' · '}
          {(submission.comments || []).length} {(submission.comments || []).length === 1 ? 'comment' : 'comments'}
        </div>
      )}

      {previewFile && (() => {
        const pIdx = uploads.findIndex(f => f.id === previewFile.id);
        return (
          <FilePreviewOverlay
            file={previewFile}
            onClose={() => setPreviewFile(null)}
            authedBlobUrl={previewBlobUrl}
            downloadUrl={previewFile.url}
            index={pIdx >= 0 ? pIdx : undefined}
            total={uploads.length}
            onPrev={pIdx > 0 ? () => setPreviewFile(uploads[pIdx - 1]) : undefined}
            onNext={pIdx >= 0 && pIdx < uploads.length - 1 ? () => setPreviewFile(uploads[pIdx + 1]) : undefined}
            items={uploads}
            thumbBlobs={thumbBlobs}
            onJump={(f) => setPreviewFile(f)}
          />
        );
      })()}

      {aiReviewFile && (
        <AiReviewIssuesModal
          open={!!aiReviewFile}
          onOpenChange={(v) => !v && setAiReviewFile(null)}
          file={aiReviewFile}
          workspaceId={workspaceId}
          taskId={taskId}
          submissionId={submission.id}
          onChanged={onChanged}
        />
      )}

      {isExpanded && (
        <SubmissionCommentThread
          comments={submission.comments}
          workspaceId={workspaceId}
          taskId={taskId}
          submissionId={submission.id}
          viewerId={viewerId}
          canTopLevel={canTopLevel}
          canReply={canReply}
          canDeleteAny={canApprove}
          isAssignee={isAssignee}
          onChanged={onChanged}
          highlightCommentId={highlightCommentId}
        />
      )}

      {/* Iter 65a Section 4 — Review action modal (reject only; 2026-07-13:
          Request-changes flows inline through the version comment bar). */}
      {reviewModal?.kind === 'reject' && (
        <ReviewActionModal
          kind="reject"
          version={submission.version}
          onClose={() => setReviewModal(null)}
          onSubmit={(payload) => doReject(payload)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Iter 65a Section 4 — Review Action Modal (Request Changes / Reject)
// Required note/reason textarea + (reject only) "reopen task" toggle.
// ──────────────────────────────────────────────────────────────────
function ReviewActionModal({ kind, version, onClose, onSubmit }) {
  const [text, setText] = useState('');
  const [alsoReopen, setAlsoReopen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isReject = kind === 'reject';
  const trimmed = text.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 2000;
  const labels = isReject ? {
    title: `Reject V${version}`,
    desc: 'Provide a clear reason. The assignee + uploader will be notified and the reason posts as a system comment.',
    field: 'Reason',
    placeholder: 'Why is this version being rejected? Be specific so the team can recover.',
    submit: 'Reject submission',
    submitClass: 'bg-red-500/15 hover:bg-red-500/25 text-red-300 border-red-500/40',
    icon: <XCircle className="h-3.5 w-3.5" />,
  } : {
    title: `Request changes on V${version}`,
    desc: 'Leave a note describing what should change. The assignee + uploader will be notified and the note posts as a system comment.',
    field: 'Note',
    placeholder: 'What needs to change? e.g. "Update headline copy and re-export at 1080p."',
    submit: 'Request changes',
    submitClass: 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border-amber-500/40',
    icon: <AlertTriangle className="h-3.5 w-3.5" />,
  };
  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const payload = isReject
        ? { reason: trimmed, alsoReopen }
        : { note: trimmed };
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-[480px] bg-card border-border/40" data-testid={`review-action-modal-${kind}`}>
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold flex items-center gap-2">
            {labels.icon}
            {labels.title}
          </DialogTitle>
          <p className="text-[11.5px] text-muted-foreground/70 mt-1">{labels.desc}</p>
        </DialogHeader>
        <div className="space-y-2.5 mt-2">
          <label className="text-[11px] font-medium text-muted-foreground/80">
            {labels.field}<span className="text-red-400 ml-0.5">*</span>
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={labels.placeholder}
            rows={4}
            maxLength={2000}
            className="bg-background/40 border-border/30 text-[12.5px]"
            data-testid={`review-action-${kind}-text`}
            autoFocus
          />
          <div className="flex justify-between text-[10px] text-muted-foreground/50">
            <span>{trimmed.length === 0 ? 'Required' : `${trimmed.length} / 2000`}</span>
            {trimmed.length > 2000 && <span className="text-red-400">Exceeds 2000 char limit</span>}
          </div>
          {isReject && (
            <label className="flex items-start gap-2 mt-1 cursor-pointer select-none rounded-md border border-border/20 px-2.5 py-2 hover:bg-foreground/[0.03]" data-testid="review-action-reject-reopen-row">
              <input
                type="checkbox"
                checked={alsoReopen}
                onChange={(e) => setAlsoReopen(e.target.checked)}
                className="mt-0.5 accent-red-400"
                data-testid="review-action-reject-reopen-checkbox"
              />
              <span>
                <span className="block text-[12px] font-medium">Reopen task to In Progress</span>
                <span className="block text-[10.5px] text-muted-foreground/60">
                  Sets the parent task back to In Progress so the assignee can resume work.
                </span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter className="mt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-9 px-4 rounded-lg border border-border/30 text-[12px] hover:bg-foreground/[0.04] disabled:opacity-50"
            data-testid={`review-action-${kind}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid || submitting}
            className={`h-9 px-4 rounded-lg text-[12px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${labels.submitClass}`}
            data-testid={`review-action-${kind}-submit`}
          >
            {submitting ? 'Saving…' : labels.submit}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────
// Main Section
// ──────────────────────────────────────────────────────────────────
export function TaskSubmissionsSection({
  workspaceId, taskId, viewerId, workspaceRole,
  focusSubmissionId, focusCommentId,
}) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [moveToReviewOpen, setMoveToReviewOpen] = useState(false);
  const [perms, setPerms] = useState(null);

  // Iter 65a Section 3 — independent collapse/expand persisted per-task.
  // Storage shape: { [submission_id]: true }. We keep a Set in state for
  // cheap O(1) lookups but read/write the localStorage key as a plain object.
  const storageKey = `submissions.expanded.${taskId || 'unknown'}`;
  const readPersisted = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return new Set(Object.keys(parsed).filter(k => parsed[k]));
    } catch { /* ignore */ }
    return null;
  };
  const [expandedIds, setExpandedIds] = useState(() => readPersisted() || new Set());
  const persistExpanded = useCallback((set) => {
    try {
      const obj = {};
      set.forEach(id => { obj[id] = true; });
      localStorage.setItem(storageKey, JSON.stringify(obj));
    } catch { /* ignore quota errors */ }
  }, [storageKey]);
  const toggleExpanded = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistExpanded(next);
      return next;
    });
  }, [persistExpanded]);

  // Role-based approximations for immediate render; backend perms endpoint is authoritative
  const roleCanApprove = ['owner', 'admin', 'manager', 'super_admin'].includes(workspaceRole || '');
  const roleCanReject = ['owner', 'admin', 'super_admin'].includes(workspaceRole || '');
  const canApprove = (perms?.can_approve ?? roleCanApprove);
  const canRemoveApproval = (perms?.can_remove_approval ?? roleCanApprove);
  const canRequestChanges = (perms?.can_request_changes ?? roleCanApprove);
  const canClearChangesRequest = (perms?.can_clear_changes_request ?? roleCanApprove);
  const canReject = (perms?.can_reject ?? roleCanReject);
  const canClearRejection = (perms?.can_clear_rejection ?? roleCanReject);
  const canTopLevel = (perms?.can_top_level_comment ?? roleCanApprove);
  const canReply = (perms?.can_reply ?? (roleCanApprove || perms?.is_assignee || false));
  const isAssignee = !!perms?.is_assignee;

  const load = useCallback(async () => {
    if (!workspaceId || !taskId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('stellar_token');
      const [subsR, permsR] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions`,
              { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions/permissions`,
              { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!subsR.ok) {
        const errBody = await subsR.json().catch(() => ({}));
        throw new Error(errBody?.detail || `HTTP ${subsR.status}`);
      }
      const body = await subsR.json();
      setSubmissions(body.submissions || []);
      setLoadError(null);
      if (permsR.ok) setPerms(await permsR.json());
    } catch (e) {
      // Never silently render "Submissions (0)" on a fetch failure —
      // surface the error so missing-access bugs are visible, not hidden.
      setSubmissions([]);
      setLoadError(e?.message || 'Could not load submissions');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, taskId]);

  // D88 — Surgical ai_review-only refresh for the AI review polling loop.
  // Fetches the submissions endpoint silently (no setLoading) and merges
  // ONLY the `ai_review` field per file in-place. The comment composer
  // tree never sees a new submission identity so its textarea keeps focus
  // + draft text. Submission/file order is preserved.
  const refreshAiReviewOnly = useCallback(async () => {
    if (!workspaceId || !taskId) return;
    try {
      const token = localStorage.getItem('stellar_token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}/submissions`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) return;
      const body = await r.json();
      const fresh = body.submissions || [];
      setSubmissions((prev) => {
        if (!prev || prev.length === 0) return fresh;
        // Build a quick lookup: submission_id → file_id → new ai_review
        const lookup = {};
        for (const s of fresh) {
          lookup[s.id] = {};
          for (const f of (s.files || [])) lookup[s.id][f.id] = f.ai_review;
        }
        // Walk previous tree; if ai_review changed, replace ONLY the file
        // object. Otherwise reuse the same reference so React skips
        // re-rendering downstream components.
        let anyChange = false;
        const merged = prev.map((s) => {
          const subLookup = lookup[s.id];
          if (!subLookup) return s;
          let fileChanged = false;
          const newFiles = (s.files || []).map((f) => {
            const newAi = subLookup[f.id];
            if (newAi === undefined) return f;
            const oldAi = f.ai_review || {};
            const sameStatus = (oldAi.status || null) === (newAi?.status || null);
            const sameReviewedAt = (oldAi.reviewed_at || null) === (newAi?.reviewed_at || null);
            const sameIssuesLen = (oldAi.issues?.length || 0) === (newAi?.issues?.length || 0);
            if (sameStatus && sameReviewedAt && sameIssuesLen) return f;
            fileChanged = true;
            return { ...f, ai_review: newAi };
          });
          if (!fileChanged) return s;
          anyChange = true;
          return { ...s, files: newFiles };
        });
        return anyChange ? merged : prev;
      });
    } catch (_) {}
  }, [workspaceId, taskId]);

  useEffect(() => { load(); }, [load]);

  // D88 — Poll while any file is `pending`. Uses the surgical merge so the
  // comment composer never remounts and the user's draft survives.
  useAiReviewPolling({
    submissions,
    onMerge: refreshAiReviewOnly,
    intervalMs: 4000,
    maxAttempts: 60,
  });

  // Default-open the newest version on first load if NOTHING is yet persisted
  // (i.e. user has never expanded/collapsed on this task before). Idempotent —
  // we never auto-expand anything else on subsequent loads.
  const newestId = submissions[0]?.id;
  const didDefaultRef = useRef(false);
  useEffect(() => {
    if (!newestId || didDefaultRef.current) return;
    didDefaultRef.current = true;
    if (!localStorage.getItem(storageKey)) {
      setExpandedIds(prev => {
        const next = new Set(prev);
        next.add(newestId);
        persistExpanded(next);
        return next;
      });
    }
  }, [newestId, storageKey, persistExpanded]);

  // Auto-expand submission deep-linked from a notification click + scroll to it
  useEffect(() => {
    if (!focusSubmissionId || submissions.length === 0) return;
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.add(focusSubmissionId);
      persistExpanded(next);
      return next;
    });
    // Scroll to the version card on next frame
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="submission-v${(submissions.find(s => s.id === focusSubmissionId) || {}).version}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [focusSubmissionId, submissions, persistExpanded]);

  const lastSubmittedAt = submissions[0]?.uploaded_at;

  const renderVersion = (sub) => (
    <SubmissionVersion
      key={sub.id}
      submission={sub}
      workspaceId={workspaceId}
      taskId={taskId}
      onDeleted={load}
      onChanged={load}
      viewerId={viewerId}
      canApprove={canApprove}
      canRemoveApproval={canRemoveApproval}
      canRequestChanges={canRequestChanges}
      canClearChangesRequest={canClearChangesRequest}
      canReject={canReject}
      canClearRejection={canClearRejection}
      canDeleteAny={canApprove}
      canTopLevel={canTopLevel}
      canReply={canReply}
      isAssignee={isAssignee}
      highlightCommentId={focusSubmissionId === sub.id ? focusCommentId : null}
      isExpanded={expandedIds.has(sub.id)}
      onToggleExpanded={() => toggleExpanded(sub.id)}
    />
  );

  return (
    <div data-testid="submissions-section">
      <div className="flex items-center justify-between mb-2">
        <span className="text-muted-foreground text-[11px] flex items-center gap-1">
          <Upload className="h-3 w-3" /> Submissions ({submissions.length})
        </span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          onClick={() => setUploadOpen(true)}
          data-testid="open-submission-upload"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Upload submission
        </Button>
      </div>

      {!loading && loadError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-4 text-center" data-testid="submissions-load-error">
          <p className="text-[11px] text-red-400">
            Could not load submissions: {loadError}
          </p>
          <Button size="sm" variant="outline" className="h-7 text-[11px] mt-2" onClick={load} data-testid="submissions-retry-btn">
            Retry
          </Button>
        </div>
      )}

      {!loading && !loadError && submissions.length === 0 && (
        <div className="rounded-lg border border-dashed border-border/30 px-3 py-6 text-center" data-testid="submissions-empty">
          <Upload className="h-5 w-5 mx-auto text-muted-foreground/40" />
          <p className="text-[11px] text-muted-foreground/60 mt-1.5">
            No submissions yet. Upload the first version to start the review cycle.
          </p>
        </div>
      )}

      {submissions.length > 0 && (
        <div className="space-y-2">
          {submissions.map(s => renderVersion(s))}
        </div>
      )}

      <SubmissionUploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSubmitted={async () => {
          await load();
          // Iter 63 Item 6 — if the task is in_progress, ask the uploader if
          // they'd like to move it to Review (which auto-pauses the timer).
          try {
            const token = localStorage.getItem('stellar_token');
            const tr = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (tr.ok) {
              const t = await tr.json();
              if (t?.status === 'in_progress') setMoveToReviewOpen(true);
            }
          } catch { /* ignore */ }
        }}
        workspaceId={workspaceId}
        taskId={taskId}
        lastSubmittedAt={lastSubmittedAt}
      />

      <Dialog open={moveToReviewOpen} onOpenChange={setMoveToReviewOpen}>
        <DialogContent className="sm:max-w-[420px] bg-card border-border/40" data-testid="submission-move-review-prompt">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-semibold">Move this task to Review?</DialogTitle>
            <p className="text-[12px] text-muted-foreground/70 mt-1">
              You just uploaded a new submission. Moving the task to <b>Review</b> will pause your timer and let approvers know it's ready.
            </p>
          </DialogHeader>
          <DialogFooter className="mt-3">
            <button
              type="button"
              onClick={() => setMoveToReviewOpen(false)}
              className="h-9 px-4 rounded-lg border border-border/30 text-[12px] hover:bg-foreground/[0.04]"
              data-testid="submission-skip-review-btn"
            >
              Skip for now
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('stellar_token');
                  await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${workspaceId}/${taskId}`,
                    {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ status: 'review' }),
                    }
                  );
                  toast.success('Moved to Review · timer paused');
                } catch {
                  toast.error('Could not move task');
                }
                setMoveToReviewOpen(false);
              }}
              className="h-9 px-4 rounded-lg bg-gold/10 hover:bg-gold/20 text-gold text-[12px] border-0"
              data-testid="submission-move-review-btn"
            >
              Yes, move to Review
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
